import { useEffect, useCallback, useRef } from 'react';
import Constants from '../constant';

/**
 * Custom hook for Google reCAPTCHA v3
 * @returns {Function} executeRecaptcha - Function to execute reCAPTCHA and get token
 */
export const useRecaptcha = () => {
    const siteKey = Constants.env.recaptchaSiteKey;
    const scriptLoadedRef = useRef(false);

    // Load reCAPTCHA script
    useEffect(() => {
        if (!siteKey) {
            console.warn('[reCAPTCHA] Site key not configured');
            return;
        }

        // Check if script is already loaded
        if (window.grecaptcha && window.grecaptcha.ready) {
            scriptLoadedRef.current = true;
            return;
        }

        // Check if script tag already exists
        const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');
        if (existingScript) {
            // Wait for script to load
            const checkScript = setInterval(() => {
                if (window.grecaptcha && window.grecaptcha.ready) {
                    scriptLoadedRef.current = true;
                    clearInterval(checkScript);
                }
            }, 100);
            return () => clearInterval(checkScript);
        }

        // Load the reCAPTCHA script
        const script = document.createElement('script');
        script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
            scriptLoadedRef.current = true;
            console.log('[reCAPTCHA] Script loaded successfully');
        };
        
        script.onerror = () => {
            console.error('[reCAPTCHA] Failed to load script');
        };
        
        document.head.appendChild(script);
    }, [siteKey]);

    /**
     * Execute reCAPTCHA and get token
     * @param {string} action - Action name (e.g., 'login', 'submit')
     * @returns {Promise<string>} reCAPTCHA token
     */
    const executeRecaptcha = useCallback(async (action = 'login') => {
        if (!siteKey) {
            console.warn('[reCAPTCHA] Site key not configured, skipping reCAPTCHA');
            return null;
        }

        // Wait for script to load if not already loaded
        const waitForScript = () => {
            return new Promise((resolve, reject) => {
                if (window.grecaptcha && window.grecaptcha.ready) {
                    resolve();
                    return;
                }

                let attempts = 0;
                const maxAttempts = 50; // 5 seconds max wait
                const checkInterval = setInterval(() => {
                    attempts++;
                    if (window.grecaptcha && window.grecaptcha.ready) {
                        clearInterval(checkInterval);
                        resolve();
                    } else if (attempts >= maxAttempts) {
                        clearInterval(checkInterval);
                        reject(new Error('reCAPTCHA script loading timeout'));
                    }
                }, 100);
            });
        };

        try {
            await waitForScript();
        } catch (error) {
            console.error('[reCAPTCHA] Script not loaded:', error);
            return null; // Return null instead of rejecting to allow graceful degradation
        }

        return new Promise((resolve, reject) => {
            window.grecaptcha.ready(() => {
                try {
                    window.grecaptcha
                        .execute(siteKey, { action })
                        .then((token) => {
                            console.log('[reCAPTCHA] Token generated for action:', action);
                            resolve(token);
                        })
                        .catch((error) => {
                            console.error('[reCAPTCHA] Error executing reCAPTCHA:', error);
                            // Return null instead of rejecting to allow graceful degradation
                            resolve(null);
                        });
                } catch (error) {
                    console.error('[reCAPTCHA] Error in reCAPTCHA execution:', error);
                    // Return null instead of rejecting to allow graceful degradation
                    resolve(null);
                }
            });
        });
    }, [siteKey]);

    return executeRecaptcha;
};

